<template>
  <div class="c-controls-resources">
    <!-- Navigation -->
    <cr-nav
      :heading="fields.heading"
      :subheading="fields.subheading"
      :categories="fields.categories"
      :versions="fields.versions"
      :queryData="queryData"
      @setCategory="setCategory"
      @toggleVersion="toggleVersion"
      @clearVersion="clearVersion"
    />

    <!-- Listing -->
    <div v-if="loading">
      <!-- Skeleton -->
      <div class="skeleton">
        <div class="skeleton__inner">
          <div class="description">
            <div class="text shine"></div>
            <div class="text shine"></div>
            <div class="text shine"></div>
          </div>
          <div class="list">
            <div class="header shine"></div>
            <div class="text shine"></div>
            <div class="text shine"></div>
            <div class="text shine"></div>
            <div class="links">
              <div class="link shine"></div>
              <div class="link shine"></div>
              <div class="link shine"></div>
              <div class="link shine"></div>
              <div class="link shine"></div>
              <div class="link shine"></div>
            </div>
            <div class="header shine"></div>
            <div class="text shine"></div>
            <div class="text shine"></div>
            <div class="text shine"></div>
            <div class="links">
              <div class="link shine"></div>
              <div class="link shine"></div>
              <div class="link shine"></div>
              <div class="link shine"></div>
              <div class="link shine"></div>
              <div class="link shine"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Controls Listing -->
    <cr-listing
      v-else
      :category="result?.category"
      :resources="result?.resources?.results?.items"
      :class="[{ loading: loading }, { error: error }]"
    />
  </div>
</template>

<script>
import { getCurrentInstance, ref } from 'vue';
import { useQuery } from '@vue/apollo-composable';
import { isEditorActive } from '@sitecore-jss/sitecore-jss-vue';
import { ControlsResourcesQuery } from './CIS-Controls-Resources.graphql';
import CISControlsResourcesNav from './CIS-Controls-Resources-Nav.vue';
import CISControlsResourcesListing from './CIS-Controls-Resources-Listing.vue';

const categoryKey = 'crc';
const versionKey = 'crv';

export default {
  name: 'CIS-Controls-Resources',
  components: {
    CrNav: CISControlsResourcesNav,
    CrListing: CISControlsResourcesListing
  },
  props: {
    fields: {
      type: Object,
      default: () => ({})
    }
  },
  setup(props) {
    const queryData = ref({
      categoryId: '',
      versionIds: []
    });

    // Find the category by Name or Id. If category isn't found then default to the first category.
    const findCategory = (categoryId) => {
      return (
        props?.fields?.categories?.find((c) => c.name === categoryId || c.id === categoryId) ??
        props?.fields?.categories[0]
      );
    };

    // Set the categoryId ref property by the category Name or Id.
    const setCategory = (categoryId) => {
      queryData.value.categoryId = findCategory(categoryId)?.id;
    };

    //Find the version object by its Name or Id.
    const findVersion = (versionId) => {
      const version = props?.fields?.versions?.find(
        (v) => v.name === versionId || v.id === versionId
      );
      return version;
    };

    const toggleVersion = (versionId) => {
      const version = findVersion(versionId);

      // Find if the version already exists in the versionIds array
      const versionIndex = queryData.value.versionIds?.findIndex((v) => v === version.id);

      //If the version already exists in the versionIds array, remove it, otherwise add it.
      if (versionIndex >= 0) {
        queryData.value.versionIds.splice(versionIndex, 1);
      } else if (version) {
        queryData.value.versionIds.push(version.id);
      }
    };

    const clearVersion = () => {
      queryData.value.versionIds = [];
    };

    const instance = getCurrentInstance();
    const route = instance?.appContext?.config?.globalProperties?.$route;

    const urlCategory = route?.query[categoryKey];
    setCategory(urlCategory);

    const queryVersions = route.query[versionKey] ?? [];
    const urlVersions = [...[queryVersions]].flat();
    urlVersions.forEach((v) => toggleVersion(v));

    const variables = () => {
      if (!queryData?.value?.categoryId) {
        return null;
      }

      const output = {
        categoryId: queryData.value.categoryId
      };

      if (Array.isArray(queryData.value.versionIds) && queryData.value.versionIds.length > 0) {
        output.fieldsEqual = [
          {
            name: 'controlsResourceFilters',
            value: queryData.value.versionIds?.join(',')
          }
        ];
      }

      return output;
    };

    const queryOptions = () => {
      return {
        prefetch: true,
        enabled: queryData.value.categoryId && queryData.value.categoryId.length > 0
      };
    };

    const { result, loading, error } = useQuery(ControlsResourcesQuery, variables, queryOptions);

    return {
      result,
      loading,
      error,
      queryData,
      setCategory,
      toggleVersion,
      clearVersion,
      findCategory,
      findVersion
    };
  },
  mounted() {
    if (!this.loading) {
      this.setUrlQueryString();
    }
  },
  updated() {
    if (!this.loading) {
      this.setUrlQueryString();
    }
  },
  methods: {
    setUrlQueryString() {
      // If this page/component is running in the experience editor then exit
      if (isEditorActive() === true || this.loading) {
        return;
      }

      // The window object isn't supported in Node's SSR, but this is OK since
      // it isn't called for SSR.
      const url = new URL(window.location);
      const searchParams = new URLSearchParams();

      const categoryName = this.findCategory(this.queryData.categoryId)?.name;
      if (categoryName) {
        searchParams.append(categoryKey, categoryName);
      }

      const versionNames = this.queryData.versionIds
        ?.map((id) => this.findVersion(id))
        ?.map((v) => v.name);
      if (Array.isArray(versionNames) && versionNames.length > 0) {
        versionNames.forEach((v) => {
          searchParams.append(versionKey, v);
        });
      }

      // Sort the search params so they are in a consistant order.
      const sortedParams = new URLSearchParams(Array.from(searchParams.entries()).sort());

      url.search = `?${sortedParams.toString()}`;
      url.hash = '';

      if (window.location.search !== url.search) {
        window.history.pushState({}, 'Search', url);
      }
    }
  }
};
</script>
<style lang="scss" scoped>
.c-controls-resources {
  .skeleton {
    &__inner {
      @include exlarge-desktop-up {
        @include padding-x-thin;
      }

      .description {
        padding: 4rem 7rem;
        background-color: $box-gray;
        // height: 23rem;

        // @include tablet-up {
        //   height: 25.5rem;
        // }

        // @include desktop-up {
        //   height: 22rem;
        // }

        // @include large-desktop-up {
        //   height: 18.5rem;
        // }

        .text {
          width: 100%;
          height: 2.6rem;
          margin-top: 0.6rem;
          margin-bottom: 1rem;
        }

        .shine {
          background: #e4e5e6;
          background-image: linear-gradient(
            to right,
            #e4e5e6 0%,
            #edeef1 20%,
            #e4e5e6 40%,
            #e4e5e6 100%
          );
          background-repeat: no-repeat;
          background-size: 80rem 10.4rem;
          position: relative;
          animation: shimmer 1s infinite linear forwards;
        }
      }

      .list {
        padding: 5.4rem 7rem;

        .header {
          width: 35%;
          height: 3.5rem;
          margin-bottom: 2rem;
        }

        .text {
          width: 100%;
          max-width: 118rem;
          height: 2rem;
          margin-top: 0.4rem;
          margin-bottom: 1rem;
        }

        .links {
          @include margin-y(3rem);
          padding-bottom: 2rem;
          border-bottom: solid 0.1rem $divider;

          @include tablet-up {
            @include margin-y(3rem, 4rem);
          }

          &:last-child {
            border-bottom: none;
            padding-bottom: 0;
            margin-bottom: 0;
          }

          .link {
            width: 50%;
            height: 2.8rem;
            margin-bottom: 1.8rem;
          }
        }

        .shine {
          background: #f6f7f8;
          background-image: linear-gradient(
            to right,
            #f6f7f8 0%,
            #edeef1 20%,
            #f6f7f8 40%,
            #f6f7f8 100%
          );
          background-repeat: no-repeat;
          background-size: 80rem 10.4rem;
          position: relative;
          animation: shimmer 1s infinite linear forwards;
        }
      }
    }
  }
}

@keyframes shimmer {
  0% {
    background-position: -20rem 0;
  }

  100% {
    background-position: min(75vw, 100rem) 0;
  }
}
</style>
